// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for the WS8a Results-graph palette (locked 2026-07-30). A
// colorblind-safe 7-series palette with a light/dark dual mode. Beyond 7 series
// the hues repeat but the line style changes (solid -> dashed -> dotted) so the
// series stay distinguishable, and the UI recommends Focus. Replaces the old
// ColorBrewer Dark2 palette, which included a pink the IP line bans.
//
// This is layout/lookup only (pure), so it unit-tests without a chart. The
// imperative shell (ResultsBand) picks the mode from the current theme and hands
// these values to the LineChart.

export type ChartMode = 'light' | 'dark';

/**
 * The 7 series colors, in order. Each entry is [light, dark]. Chosen for
 * colorblind-safety and to read on both a light and a dark chart ground.
 * Order: cyan, amber, violet, green, slate, blue, red. NO fuchsia/neon pink.
 */
export const RESULTS_SERIES_COLORS: ReadonlyArray<readonly [string, string]> = [
  ['#0E7490', '#22D3EE'], // 1 cyan
  ['#B45309', '#FBBF24'], // 2 amber
  ['#6D28D9', '#A78BFA'], // 3 violet
  ['#15803D', '#4ADE80'], // 4 green
  ['#334155', '#CBD5E1'], // 5 slate
  ['#1D4ED8', '#60A5FA'], // 6 blue
  ['#B91C1C', '#F87171'], // 7 red
] as const;

export const RESULTS_SERIES_COUNT = RESULTS_SERIES_COLORS.length; // 7

/**
 * Line styles for the hue-repeat cycles. `undefined` = solid (the first 7
 * series), then dashed, then dotted -- an SVG stroke-dasharray. So series 8-14
 * reuse the 7 hues dashed, 15-21 dotted, and so on. (This chart dashing is
 * unrelated to the canvas dashed=delay convention -- a different surface.)
 */
export const RESULTS_LINE_DASHES: ReadonlyArray<string | undefined> = [undefined, '6 3', '2 3'];

/** The flat color list for a mode (round-robin base for `toChartSeries`). */
export function resultsPalette(mode: ChartMode): string[] {
  const idx = mode === 'dark' ? 1 : 0;
  return RESULTS_SERIES_COLORS.map((pair) => pair[idx]);
}

/** The dash pattern for the i-th series (undefined = solid). */
export function resultsDash(index: number): string | undefined {
  const cycle = Math.floor(index / RESULTS_SERIES_COUNT) % RESULTS_LINE_DASHES.length;
  return RESULTS_LINE_DASHES[cycle];
}

/** The dash list aligned to the palette length, so `toChartSeries` advances one
 *  dash cycle each time the hues wrap. */
export const RESULTS_DASHES_BY_CYCLE = RESULTS_LINE_DASHES;

/** True when there are more series than distinct hues -- surface the
 *  "7+ series, Focus recommended" hint. */
export function focusRecommended(seriesCount: number): boolean {
  return seriesCount > RESULTS_SERIES_COUNT;
}
